import { useEffect, useRef } from "react";
import { useLang } from "../i18n";

export function useScrollReveal(staggerMs = 100) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { lang } = useLang();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Mark all current .reveal elements as visible if already in viewport
    const revealAll = () => {
      container.querySelectorAll<HTMLElement>(".reveal").forEach((el, i) => {
        if (!prefersReducedMotion) {
          el.style.transitionDelay = `${i * staggerMs}ms`;
        }
        el.classList.add("visible");
      });
    };

    if (prefersReducedMotion) {
      revealAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealAll();
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 }
    );

    // Reset reveal state for fresh elements (e.g. after lang switch)
    container.querySelectorAll<HTMLElement>(".reveal").forEach((el) => {
      if (!el.classList.contains("visible")) {
        // Check if container is already in viewport
        const rect = container.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView) {
          el.style.transitionDelay = "0ms";
          el.classList.add("visible");
        }
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [staggerMs, lang]);

  return containerRef;
}
